import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { TFunction } from 'i18next';

import { StatusChip } from '@/components/Cards/Chips/StatusChip';
import { IFalabellaOrder } from '@/types/plugins/falabella';
import { formatDate } from '@/utils/common';

import {
  get99mStatusBg,
  get99mStatusColor,
  get99mStatusLabel,
  getFalabellaModalityLabel,
  getFalabellaStatusBg,
  getFalabellaStatusColor,
  getFalabellaStatusLabel,
} from './constants';

export const falabellaColumns = (
  t: TFunction,
  onOpenDetails: (order: IFalabellaOrder) => void,
  onDownloadLabel: (orderId: string) => void,
  onCreateOrder: (orderId: string) => void,
  creatingOrderId?: string | null,
): GridColDef<IFalabellaOrder>[] => [
  {
    field: 'internalKey',
    headerName: t('columns.orderFalabella', 'N° de Venta Falabella'),
    flex: 1.2,
    minWidth: 170,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: (params: GridRenderCellParams<IFalabellaOrder>) => {
      const internalKey = params.row.internalKey || params.row.lpn || '-';

      return (
        <Tooltip title={internalKey} placement="top">
          <Typography
            variant="body2"
            noWrap
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontWeight: 'bold',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'inline-block',
              textAlign: 'center',
            }}
            onClick={() => onOpenDetails(params.row)}
          >
            {internalKey}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: 'createdAt',
    headerName: t('columns.createdAt', 'Fecha de pedido'),
    flex: 1,
    minWidth: 130,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: (params: GridRenderCellParams<IFalabellaOrder>) => (
      <Typography
        variant="body2"
        display="inline"
        sx={{ color: (theme) => theme.palette.grey[600] }}
      >
        {params.row.createdAt ? formatDate(params.row.createdAt, 'dd/MM/yyyy') : '-'}
      </Typography>
    ),
  },
  {
    field: 'statusFalabella',
    headerName: t('columns.statusFalabella', 'Estatus Falabella'),
    flex: 1,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: (params: GridRenderCellParams<IFalabellaOrder>) => (
      <Box>
        <StatusChip
          status={getFalabellaStatusLabel(params.row.statusFalabella)}
          color={getFalabellaStatusColor(params.row.statusFalabella)}
          bg={getFalabellaStatusBg(params.row.statusFalabella)}
        />
      </Box>
    ),
  },
  {
    field: 'tracking99m',
    headerName: t('columns.tracking99m', 'Nº de seguimiento'),
    flex: 1,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: (params: GridRenderCellParams<IFalabellaOrder>) => {
      const hasTracking = Boolean(
        params.row.tracking99m && params.row.tracking99m.trim().length > 0,
      );
      return (
        <Tooltip title={hasTracking ? params.row.tracking99m! : ''} placement="top">
          <Typography
            variant="body2"
            noWrap
            display="inline"
            sx={{
              fontWeight: hasTracking ? 'bold' : 'normal',
              color: (theme) =>
                hasTracking ? theme.palette.primary.main : theme.palette.grey[500],
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {hasTracking ? params.row.tracking99m : '-'}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    field: 'status99m',
    headerName: t('columns.status99m', 'Estatus 99minutos'),
    flex: 1,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: (params: GridRenderCellParams<IFalabellaOrder>) => (
      <Box>
        <StatusChip
          status={get99mStatusLabel(params.row.status99m)}
          color={get99mStatusColor(params.row.status99m)}
          bg={get99mStatusBg(params.row.status99m)}
        />
      </Box>
    ),
  },
  {
    field: 'modality',
    headerName: t('columns.modality', 'Modalidad'),
    flex: 1,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    renderCell: (params: GridRenderCellParams<IFalabellaOrder>) => {
      const modality = params.row.modality || params.row.shippingProviderType || 'regular';
      const isDirect = modality.toLowerCase() === 'direct';

      return (
        <Chip
          label={getFalabellaModalityLabel(modality)}
          size="small"
          color={isDirect ? 'primary' : 'default'}
          variant="outlined"
        />
      );
    },
  },
  {
    field: 'actions',
    headerName: t('columns.actions', 'Acciones'),
    flex: 0.6,
    minWidth: 90,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    resizable: false,
    renderCell: (params: GridRenderCellParams<IFalabellaOrder>) => {
      const hasTracking = Boolean(
        params.row.tracking99m && params.row.tracking99m.trim().length > 0,
      );
      const trackingId = params.row.tracking99m || '';
      const orderIdentifier = params.row.orderFalabella || params.row.id;
      const isCreating = creatingOrderId === orderIdentifier;
      const isDirect =
        (params.row.modality || params.row.shippingProviderType || '').toLowerCase() === 'direct';
      const cleanStatus = (params.row.statusFalabella || '').toLowerCase().trim();
      const rawInternalKey = (params.row.internalKey || params.row.lpn || '').trim();
      const hasInternalKey = Boolean(
        rawInternalKey &&
        rawInternalKey !== params.row.orderFalabella &&
        rawInternalKey !== params.row.id,
      );
      const isReadyToShip = cleanStatus === 'ready_to_ship';
      const isPending = cleanStatus === 'pending';

      return (
        <Box
          sx={{ display: 'flex', gap: '0.25rem', alignItems: 'center', justifyContent: 'center' }}
        >
          {hasTracking ? (
            <Tooltip title={t('actions.downloadLabel', 'Descargar Etiqueta')} placement="top">
              <span>
                <IconButton
                  color="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownloadLabel(trackingId);
                  }}
                >
                  <CloudDownloadOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : !isDirect ? (
            <Tooltip
              title={t(
                'actions.invalidOrderType',
                'Tipo de orden no válida para creación en 99minutos (solo órdenes direct)',
              )}
              placement="top"
            >
              <span>
                <IconButton color="primary" size="small" disabled={true}>
                  <AddCircleOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : isPending && !hasInternalKey ? (
            <Tooltip
              title={t(
                'actions.pendingMissingInternalKey',
                "Las órdenes en estatus 'Pendiente' requieren contar con internalKey (LPN) para su creación",
              )}
              placement="top"
            >
              <span>
                <IconButton color="primary" size="small" disabled={true}>
                  <AddCircleOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : !isReadyToShip && !isPending ? (
            <Tooltip
              title={t(
                'actions.invalidOrderStatus',
                "Solo se permite crear órdenes en estatus 'Listo para envío' o 'Pendiente'",
              )}
              placement="top"
            >
              <span>
                <IconButton color="primary" size="small" disabled={true}>
                  <AddCircleOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={t('actions.createOrder', 'Crear orden en 99minutos')} placement="top">
              <span>
                <IconButton
                  color="primary"
                  size="small"
                  disabled={isCreating}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateOrder(orderIdentifier);
                  }}
                >
                  {isCreating ? (
                    <CircularProgress size={18} />
                  ) : (
                    <AddCircleOutlineOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      );
    },
  },
];
